import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Mic, MicOff, Volume2, VolumeX, Loader2, CheckCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import { useVoice } from "@/hooks/useVoice";

const MODES = {
  freundlich: { label: "Freundlich", desc: "Einfühlsam und unterstützend" },
  pruefungsnah: { label: "Prüfungsnah", desc: "Wie in einer echten MPU" },
  kritisch: { label: "Kritisch", desc: "Hinterfragend und bohrend" },
  intensiv: { label: "Intensiv", desc: "Sehr direkt und konfrontativ" },
  unterstuetzend: { label: "Unterstützend", desc: "Coachend und reflektionsfördernd" },
};

const TOPICS = {
  trinkverlauf: "Trinkverlauf und Konsumverhalten",
  einsicht: "Einsicht und Reue",
  veraenderung: "Veränderungsmotivation",
  eigenverantwortung: "Eigenverantwortung",
  rueckfallpraevention: "Rückfallprävention",
  lebenssituation: "Allgemeine Lebenssituation",
  freie_simulation: "Freie MPU-Simulation",
};

export default function TrainingSessionPage() {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(urlSessionId || null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("pruefungsnah");
  const [topic, setTopic] = useState("freie_simulation");
  const [status, setStatus] = useState("idle");
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Load existing session
  useEffect(() => {
    if (!urlSessionId) return;
    const loadSession = async () => {
      try {
        const res = await api.get(`/training/sessions/${urlSessionId}`);
        setSessionId(urlSessionId);
        setMessages(res.data.messages || []);
        setMode(res.data.mode);
        setTopic(res.data.topic);
        setStatus(res.data.status);
      } catch {
        toast.error("Session konnte nicht geladen werden");
        navigate("/dashboard");
      }
    };
    loadSession();
  }, [urlSessionId, navigate]);

  const { speak, stop: stopVoice, isSpeaking, isLoading: voiceLoading, voiceProvider } = useVoice();

  // TTS for AI messages — uses ElevenLabs if available, browser as fallback
  useEffect(() => {
    if (!ttsEnabled || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === "assistant") {
      // 0.7s Denkpause vor dem Sprechen (natürlicher)
      setTimeout(() => speak(lastMsg.content), 700);
    }
  }, [messages, ttsEnabled, speak]);

  const startSession = async () => {
    setStatus("loading");
    try {
      const res = await api.post("/training/sessions", { mode, topic });
      setSessionId(res.data.session_id);
      setMessages(res.data.messages || []);
      setStatus("active");
      navigate(`/training/${res.data.session_id}`, { replace: true });
    } catch {
      toast.error("Session konnte nicht gestartet werden");
      setStatus("idle");
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || sending) return;
    const text = message.trim();
    setMessage("");
    setSending(true);
    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    try {
      const res = await api.post(`/training/sessions/${sessionId}/message`, { content: text });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.ai_message.content, timestamp: res.data.ai_message.timestamp }]);
    } catch {
      toast.error("Nachricht konnte nicht gesendet werden");
    } finally {
      setSending(false);
    }
  };

  const completeSession = async () => {
    if (messages.length < 4) { toast.error("Führen Sie mindestens 2 Gesprächsrunden, bevor Sie die Session abschließen"); return; }
    setCompleting(true);
    try {
      await api.post(`/training/sessions/${sessionId}/complete`);
      toast.success("Session abgeschlossen! Analyse wird angezeigt.");
      navigate(`/analysis/${sessionId}`);
    } catch {
      toast.error("Session konnte nicht abgeschlossen werden");
    } finally {
      setCompleting(false);
    }
  };

  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { toast.error("Spracherkennung wird in Ihrem Browser nicht unterstützt"); return; }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "de-DE";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setMessage(prev => prev + transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  // Session Setup View
  if (!sessionId && status !== "loading") {
    return (
      <div className="page-transition max-w-2xl mx-auto" data-testid="training-setup">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight">Neues Training starten</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">Wählen Sie Modus und Thema für Ihre Übungssession</p>
        </div>
        <Card className="border-border/50">
          <CardContent className="pt-6 space-y-6">
            <div>
              <label className="font-body text-sm font-medium mb-2 block">Gesprächsmodus</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-11 font-body" data-testid="mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MODES).map(([k, v]) => (
                    <SelectItem key={k} value={k} className="font-body">
                      <span className="font-medium">{v.label}</span>
                      <span className="text-muted-foreground ml-2 text-xs">- {v.desc}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-sm font-medium mb-2 block">Themenbereich</label>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger className="h-11 font-body" data-testid="topic-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TOPICS).map(([k, v]) => (
                    <SelectItem key={k} value={k} className="font-body">{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-body text-sm font-medium">Hinweis</p>
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    Dieses Training ist eine Übungssimulation und ersetzt keine professionelle MPU-Beratung oder Therapie.
                  </p>
                </div>
              </div>
            </div>
            <Button className="w-full h-12 font-body text-base bg-primary text-primary-foreground hover:bg-primary/90" onClick={startSession} data-testid="start-session-btn">
              Training starten
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="session-loading">
        <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
        <p className="font-body text-muted-foreground">Session wird vorbereitet...</p>
      </div>
    );
  }

  // Chat View
  return (
    <div className="page-transition flex flex-col h-[calc(100vh-8rem)]" data-testid="training-chat">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border/50 mb-4">
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="font-body text-xs">{MODES[mode]?.label || mode}</Badge>
          <Badge variant="outline" className="font-body text-xs">{TOPICS[topic] || topic}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => {
            if (isSpeaking || voiceLoading) { stopVoice(); }
            else { setTtsEnabled(!ttsEnabled); }
          }} className={`gap-1 ${isSpeaking ? "text-primary" : ""}`} data-testid="tts-toggle">
            {voiceLoading ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> :
             isSpeaking ? <Volume2 className="w-4 h-4 text-primary animate-pulse" /> :
             ttsEnabled ? <Volume2 className="w-4 h-4 text-primary" /> :
             <VolumeX className="w-4 h-4 text-muted-foreground" />}
            <span className="hidden sm:inline font-body text-xs">
              {voiceLoading ? "Lädt..." : isSpeaking ? "Stopp" : ttsEnabled ? (voiceProvider === "elevenlabs" ? "Premium" : "An") : "Aus"}
            </span>
          </Button>
          {status === "active" && (
            <Button variant="outline" size="sm" onClick={completeSession} disabled={completing} className="font-body gap-1 text-xs" data-testid="complete-session-btn">
              {completing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Session beenden & Analyse
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-4 pb-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`message-${i}`}>
              <div className={`max-w-[80%] p-4 ${msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}`}>
                <p className="font-body text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                {ttsEnabled && msg.role === "assistant" && i === messages.length - 1 && (isSpeaking || voiceLoading) && (
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/30">
                    {voiceLoading ? <Loader2 className="w-3 h-3 animate-spin text-primary" /> : <Volume2 className="w-3 h-3 text-primary animate-pulse" />}
                    <span className="font-body text-xs text-primary">{voiceLoading ? "Audio wird geladen..." : "Wird gesprochen..."}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="chat-bubble-ai p-4 flex gap-1.5">
                <div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      {status === "active" && (
        <div className="pt-4 border-t border-border/50">
          <div className="flex gap-2">
            <Button
              variant={isListening ? "destructive" : "outline"}
              size="icon"
              className={`flex-shrink-0 h-11 w-11 ${isListening ? "voice-recording" : ""}`}
              onClick={toggleListening}
              data-testid="voice-input-btn"
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
            <Input
              value={message}
              onChange={e => setMessage(e.target.value.slice(0, 2000))}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Ihre Antwort eingeben..."
              className="h-11 font-body"
              disabled={sending}
              maxLength={2000}
              data-testid="message-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!message.trim() || sending}
              className="flex-shrink-0 h-11 w-11 bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="font-body text-xs text-muted-foreground mt-2 text-center">
            Tipp: Antworten Sie ausführlich und ehrlich - wie in einer echten MPU-Situation
            {message.length > 1800 && <span className="text-warning ml-2">({message.length}/2000)</span>}
          </p>
        </div>
      )}
    </div>
  );
}
